<script lang="ts">
  import * as DropdownMenu from "$lib/components/ui/dropdown-menu";
  import * as AlertDialog from "$lib/components/ui/alert-dialog";
  import { Button } from "$lib/components/ui/button";
  import { Loader2, Mail } from "lucide-svelte";
  import { cn } from "$lib/utils";
  import { createEventDispatcher } from "svelte";
  import { EnrichmentAPI } from "$lib/api/enrichment";
  import { CREDITS_PER } from "$lib/data/pricing";
  import { BillingAPI } from "$lib/api/billing";
  import { authStore as Current } from "$lib/stores";
  import { Portal } from "bits-ui";

  const { entityId, type, displayName, className, children, loggedOutCallback } = $props<{
    entityId: string | number;
    type: "person" | "company";
    displayName: string;
    className?: string;
    children: any;
    loggedOutCallback?: () => void;
  }>();

  let isOpen = $state(false);
  let isLoading = $state(false);
  let error = $state<string | null>(null);
  let alertDialogOpen = $state(false);
  let alertMessage = $state("");

  const dispatch = createEventDispatcher();
  const creditCost = $derived(type === "person" ? CREDITS_PER.EMAIL : CREDITS_PER.EMAIL);

  async function showTemporaryState(message: string) {
    error = message;
    await new Promise((resolve) => setTimeout(resolve, 3000));
    error = null;
    isOpen = false;
  }

  async function fetchEmail() {
    isLoading = true;
    error = null;

    try {
      // Check credits first
      const canPerformAction = await BillingAPI.checkCreditsAvailability("search_email_export", 1);
      if (!canPerformAction) {
        alertMessage = "You don't have enough credits to perform this action.";
        alertDialogOpen = true;
        isLoading = false;
        isOpen = false;
        return;
      }

      // Attempt to request email
      const response = await EnrichmentAPI.requestEmail(entityId, type);
      let email = response.email;

      // If "Not available", show "not available" message, then close.
      if (!email || email === "" || email === "Not available") {
        isLoading = false;
        await showTemporaryState("Email not available");
        return;
      }

      // Otherwise, we got a real email => dispatch the update and close
      logger.log("Email enrichment response", response);
      dispatch("emailUpdate", {
        email,
        entityId,
        type,
      });

      isOpen = false;
    } catch (e) {
      // If the error message is specifically "Request timed out", show that for 3s
      if (e instanceof Error && e.message === "Request timed out") {
        isLoading = false;
        await showTemporaryState("Request timed out");
      } else {
        error = e instanceof Error ? e.message : "Failed to fetch email";
        console.error("Failed to fetch email:", e);
      }
    } finally {
      isLoading = false;
    }
  }

  let isEnoughCredits = $derived($Current.isLoggedIn && ($Current.user?.credits?.total || 0) >= CREDITS_PER.EMAIL);
</script>

<DropdownMenu.Root
  controlledOpen
  open={isOpen}
  onOpenChange={(open) => {
    if (!$Current.isLoggedIn) {
      loggedOutCallback?.();
    } else {
      isOpen = open;
    }
  }}
>
  <DropdownMenu.Trigger>
    {@render children()}
  </DropdownMenu.Trigger>

  <!-- Dropdown Content -->
  <Portal>
    <DropdownMenu.Content
      side="bottom"
      align="center"
      sideOffset={4}
      class={cn(
        "z-50",
        "overflow-visible",
        "w-56 !rounded-base p-2 shadow-lg",
        "bg-white dark:bg-theme-background-tertiary",
        "border border-theme-gray-200 dark:border-white/10",
        "text-black dark:text-white",
      )}
    >
      <div class="space-y-1 p-1">
        {#if !error}
          <Button
            class={cn(
              "w-full !rounded-base p-1.5 text-xs",
              "dark:bg-theme-background-secondary",
              "dark:text-theme-gray-600",
              "dark:border dark:border-white/10",
              "font-normal dark:hover:bg-theme-background-primary dark:hover:text-theme-gray-800",
              isLoading || (!isEnoughCredits && "dark:opacity-50"),
            )}
            onclick={fetchEmail}
            disabled={isLoading || !isEnoughCredits}
          >
            {#if isLoading}
              <Loader2 class="h-4 w-4 animate-spin dark:text-theme-gray-500" />
              <span class="dark:text-theme-gray-500">Enriching email...</span>
            {:else}
              <Mail class="h-4 w-4 dark:text-theme-gray-500" strokeWidth={1} />
              {#if isEnoughCredits}
                Enrich email <span class="ml-1 rounded-full bg-theme-green px-1 py-0 text-2xs font-medium text-black"
                  >{creditCost} credits</span
                >
              {:else}
                <span class="dark:text-theme-gray-500">Not enough credits</span>
              {/if}
            {/if}
          </Button>

          <p class="flex items-center justify-center gap-0.5 text-left text-xs text-theme-text-secondary">
            <!-- <CircleAlert class="h-4 w-4" strokeWidth={1.5}/> -->
            <span class="text-center leading-tight"> Credits will only be used if the request is successful. </span>
          </p>
        {/if}

        <!-- Error/Notice Message -->
        {#if error}
          <p class="text-center text-sm text-theme-text-secondary">{error}</p>
        {/if}
      </div>
    </DropdownMenu.Content>
  </Portal>
</DropdownMenu.Root>

<!-- Alert Dialog for insufficient credits or other bigger messages -->
<AlertDialog.Root bind:open={alertDialogOpen}>
  <Portal>
    <AlertDialog.Content
      class={cn(
        "p-6 sm:max-w-[425px]",
        "bg-white dark:bg-theme-background-tertiary",
        "border border-theme-gray-200 dark:border-white/10",
        "shadow-lg",
      )}
    >
      <AlertDialog.Header>
        <AlertDialog.Title class="text-xl font-semibold text-black dark:text-white">Oh no!</AlertDialog.Title>
        <AlertDialog.Description class="mt-2 text-black/60 dark:text-white/60">
          {alertMessage}
        </AlertDialog.Description>
      </AlertDialog.Header>
      <AlertDialog.Footer class="mt-4">
        <AlertDialog.Action
          class={cn(
            "!rounded-base px-4 py-2",
            "bg-theme-gray-50 dark:bg-black/20",
            "text-black dark:text-white",
            "border border-theme-gray-200 dark:border-white/10",
            "hover:bg-theme-gray-100 dark:hover:bg-black/30",
            "hover:border-theme-gray-300 dark:hover:border-white/20",
            "text-sm",
          )}
          onclick={() => (alertDialogOpen = false)}
        >
          Close
        </AlertDialog.Action>
      </AlertDialog.Footer>
    </AlertDialog.Content>
  </Portal>
</AlertDialog.Root>
